import {
  setExpertiseEditingIndex,
  // setExpertise,
  setExpertiseTitle,
  addNewExpertise,
  removeExpertise,
  setExpertiseErrors,
  addNewExpertiseTag,
  removeExpertiseTag,
  setExpertiseTag,
  setExpertiseTagLevel,
  setBecomeExpertIn,
  setBecomeExpertInEditingIndex,
  addNewBecomeExpertIn,
  removeBecomeExpertInItem,
  setState,
  ImGoodAtAction
} from './store/Actions'

import { ImGoodAtState, imGoodAtInitialState, IExpertiseError, IFlatExpertise } from './store/State'

import { omit } from 'lodash'

import imGoodAtReducer from './store/Reducer'

import { IResponsesValue, IExpertise, IQuestions, IExpertiseTag } from 'types'

import { validateExpertise } from './StateUtils'

import ServiceProviderBase from 'lib/ServiceProviderBase'

export class ImGoodAtService extends ServiceProviderBase<ImGoodAtState, ImGoodAtAction> {
  constructor() {
    super(imGoodAtInitialState)
  }

  reduce(state: ImGoodAtState, action: ImGoodAtAction): ImGoodAtState {
    return imGoodAtReducer(state, action)
  }

  reset(): void {
    this.state = imGoodAtInitialState
  }

  mapToState(responses: IResponsesValue, questions: IQuestions) {
    const expertise = responses.expertise || ([] as IExpertise[])

    const flattening = expertise.reduce(
      (acc, exp, index) => {
        return {
          errors: {
            ...acc.errors,
            [index]: validateExpertise(exp, questions['expertise']['rules'])
          },
          expertiseMap: {
            ...acc.expertiseMap,
            [index]: {
              title: exp.title,
              tags: (exp.tags || []).reduce(
                (tagAcc: { [key: string]: IExpertiseTag }, tag: IExpertiseTag, tagIdx: number) => ({
                  ...tagAcc,
                  [tagIdx]: tag
                }),
                {}
              )
            }
          }
        }
      },
      { errors: {}, expertiseMap: {} } as { errors: IExpertiseError; expertiseMap: { [key: string]: IFlatExpertise } }
    )

    const becomeExpertIn = (responses.becomeExpertIn || ([] as string[])).reduce(
      (acc, item, index) => ({
        ...acc,
        [index]: item
      }),
      {} as { [key: string]: string }
    )

    this.dispatch(
      setState({
        ...imGoodAtInitialState,
        expertise: flattening.expertiseMap,
        expertiseErrors: flattening.errors,
        becomeExpertIn
      })
    )

    if (expertise.length === 0) {
      this.dispatch(addNewExpertise())
    }
  }

  mapFlatExpertiseToExpertise(flatExpertise: IFlatExpertise): IExpertise {
    return {
      title: flatExpertise.title,
      tags: Object.entries(flatExpertise.tags).map(([, tag]) => ({
        title: tag.title,
        level: tag.level
      }))
    }
  }

  validateExpertise(index: number, questions: IQuestions): Boolean {
    const expertise = this.mapFlatExpertiseToExpertise(this.state.expertise[index])

    const hasError = validateExpertise(expertise, questions['expertise']['rules'])

    this.dispatch(setExpertiseErrors({ [index]: hasError }))

    return !hasError
  }

  validateAllExpertises(questions: IQuestions): boolean {
    const validations = Object.keys(this.state.expertise).map((_item, index) => {
      return this.validateExpertise(index, questions)
    })

    return validations.every(it => it)
  }

  mapStateToExpertise(ignoreInvalid: boolean = false): IExpertise[] {
    let entries = Object.entries(this.state.expertise)

    if (ignoreInvalid) {
      entries = entries.filter(([key]) => this.state.expertiseErrors[key] !== true)
    }

    return entries.map(([, value]) => this.mapFlatExpertiseToExpertise(value))
  }

  mapStateToBecomeExpertIn(ignoreInvalidRecords: boolean = false): string[] {
    let entries = Object.entries(this.state.becomeExpertIn)

    if (ignoreInvalidRecords) {
      entries = entries.filter(([, value]) => !!value && value.length > 0)
    }
    return entries.map(([, value]) => value)
  }

  setExpertiseEditingIndex(index: number, questions: IQuestions) {
    const currentIndex = this.state.expertiseEditingIndex

    if (currentIndex >= 0) {
      this.validateExpertise(currentIndex, questions)
    }

    this.dispatch(setExpertiseEditingIndex(index))
  }

  setExpertiseTitle(index: number, title: string, questions: IQuestions) {
    this.dispatch(setExpertiseTitle(index, title))
    this.validateExpertise(index, questions)
  }

  addNewExpertise(questions: IQuestions) {
    const currentIndex = this.state.expertiseEditingIndex

    if (currentIndex >= 0) {
      this.validateExpertise(currentIndex, questions)
    }

    if (this.state.expertiseErrors[currentIndex]) {
      return
    }

    this.dispatch(addNewExpertise())
  }

  removeExpertise(index: number) {
    this.dispatch(removeExpertise(index))
    this.dispatch(setExpertiseErrors(omit(this.state.expertiseErrors, [index.toString()])))
  }

  addExpertiseTag(index: number) {
    this.dispatch(addNewExpertiseTag(index))
  }

  removeExpertiseTag(index: number, tagIndex: number, questions: IQuestions) {
    this.dispatch(removeExpertiseTag(index, tagIndex))
    this.validateExpertise(index, questions)
  }

  setExpertiseTagTitle(index: number, tagIndex: number, title: string, questions: IQuestions) {
    this.dispatch(setExpertiseTag(index, tagIndex, title))
    this.validateExpertise(index, questions)
  }

  setExpertiseTagLevel(index: number, tagIndex: number, level: number, questions: IQuestions) {
    this.dispatch(setExpertiseTagLevel(index, tagIndex, level))
    this.validateExpertise(index, questions)
  }

  setBecomeExpertInItem(index: number, value: string) {
    this.dispatch(setBecomeExpertIn(index, value))
  }

  setBecomeExpertInEditingIndex(index: number) {
    this.dispatch(setBecomeExpertInEditingIndex(index))
  }

  addNewBecomeExpertIn() {
    this.dispatch(addNewBecomeExpertIn())
  }

  removeBecomeExpertInItem(index: number) {
    this.dispatch(removeBecomeExpertInItem(index))
  }

  onSubmitExpertises(): IExpertise[] {
    this.dispatch(setExpertiseEditingIndex(-1))
    return this.mapStateToExpertise()
  }
}

let instance: ImGoodAtService

export default (() => {
  if (!instance) {
    instance = new ImGoodAtService()
  }
  return instance
})()
